
import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

interface UserResource {
  id: string
  title: string
  description: string | null
  module: string | null
  domain: string | null
  priority: boolean
  file_url: string | null
  created_at: string
  status?: string
}

export const useUserResources = () => {
  const [userResources, setUserResources] = useState<UserResource[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  const fetchUserResources = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session) {
        setLoading(false)
        return
      }

      const { data: userResourcesData, error: userError } = await supabase
        .from('user_resources')
        .select('*')
        .eq('user_id', session.user.id)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false })

      if (userError) throw userError
      setUserResources(userResourcesData || [])
    } catch (error: any) {
      console.error('Error fetching user resources:', error)
      toast({
        title: "Error",
        description: "Failed to load user resources",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const addResourceToMyResources = async (adminResource: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return false

      // Check if resource already exists for this user
      const { data: existingResource } = await supabase
        .from('user_resources')
        .select('id')
        .eq('user_id', session.user.id)
        .eq('title', adminResource.title)
        .single()

      if (existingResource) {
        toast({
          title: "Resource already added",
          description: "This resource is already in your collection",
          variant: "destructive",
        })
        return false
      }

      const { error } = await supabase
        .from('user_resources')
        .insert({
          title: adminResource.title,
          description: adminResource.description,
          module: adminResource.module,
          domain: adminResource.domain,
          file_url: adminResource.file_url,
          priority: false,
          user_id: session.user.id
        })

      if (error) throw error

      toast({ title: "Resource added to My Resources" })
      fetchUserResources() // Refresh the list
      return true
    } catch (error: any) {
      console.error('Error adding resource:', error)
      toast({
        title: "Error",
        description: "Failed to add resource",
        variant: "destructive",
      })
      return false
    }
  }

  const updateResourceStatus = async (resourceId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('user_resources')
        .update({ status })
        .eq('id', resourceId)

      if (error) throw error

      toast({ title: `Resource moved to ${status}` })
      fetchUserResources() // Refresh the list
      return true
    } catch (error: any) {
      console.error('Error updating resource status:', error)
      toast({
        title: "Error",
        description: "Failed to update resource",
        variant: "destructive",
      })
      return false
    }
  }

  const isResourceAdded = (adminResourceId: string, adminResourceTitle: string) => {
    return userResources.some(resource => 
      resource.title === adminResourceTitle
    )
  }

  // Filter functions
  const getActiveResources = () => {
    return userResources.filter(resource => !resource.status || resource.status === 'active')
  }

  const getPriorityResources = () => {
    return userResources.filter(resource => resource.priority && (!resource.status || resource.status === 'active'))
  }

  const getArchivedResources = () => {
    return userResources.filter(resource => resource.status === 'archived')
  }

  useEffect(() => {
    fetchUserResources()

    // Set up real-time subscription for user_resources
    const channel = supabase
      .channel('user-resources-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_resources'
        },
        () => {
          fetchUserResources()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [])

  return {
    userResources,
    loading,
    addResourceToMyResources,
    updateResourceStatus,
    isResourceAdded,
    getActiveResources,
    getPriorityResources,
    getArchivedResources,
    refreshUserResources: fetchUserResources
  }
}
